import { PrecheckRequest, PrecheckResponse, Table, TableTotal } from './../_interface/table';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiResponse, GenericItemApiResponse, GenericListApiResponse } from 'src/app/core/_interface/apiResponse';
import { map, tap } from 'rxjs/operators';
import { ICompany } from 'src/app/core/_interface/company';
import { Payment } from '../_interface/payment';
import { ChargeOrder, ChargeParams, ChargeTable, ChargeTableResponse } from '../_interface/charge';
import { Client } from '../_interface/client';
import { WaiterCall } from '../_interface/waiterCall';
import { NGXLogger } from 'ngx-logger';
import { SHELL_APP_CONFIG } from '../_interface/shellAppConfig';
import { Customer } from '../_interface/order';
import { ReliableHttpService } from './reliable-http.service';
import { accessDeniedDescriptions } from '../_helpers/accessDeniedDescriptions';
import { useUnifiedCharge } from '../_helpers/backendCompat';

@Injectable({
  providedIn: 'root'
})
export class TableService {



  saved = new Subject<Table>();
  deleted = new Subject<Table>();

  constructor(private http: HttpClient,
    private logger: NGXLogger,
    private reliableHttp: ReliableHttpService
  ) {

  }


  precheck(data: PrecheckRequest) {
    const url = environment.apiBase + `table/precheck`;

    return this.http.post<GenericItemApiResponse<PrecheckResponse>>(url, data,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.precheck } }).pipe(
      map(val => {
        if (val.success) {
          return val.item;
        }

        throw val;
      })
    );
  }


  get(companyID: number): Observable<Table[] | undefined> {
    const url = environment.apiBase + `table/get?companyID=${companyID}`;
    return this.http.get<GenericListApiResponse<Table>>(url,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.tableList } })
      .pipe(
        map(response => {
          if (response.success) {
            return response.items;
          }
          return undefined;
        })
      );
  }

  assignedTables(userID: number, companyID: number) {
    const url = environment.apiBase + `table/assignedTables?companyID=${companyID}&userID=${userID}`;
    return this.http.get<GenericListApiResponse<Table>>(url)
      .pipe(
        map(response => {
          if (response.success) {
            return response.items;
          }
          return undefined;
        })
      );
  }

  assignTables(companyID: number, userID: number, tableIDs: number[]) {
    const url = environment.apiBase + `table/assignTables`;
    var data = {
      companyID: companyID,
      userID: userID,
      tableIDs: tableIDs
    }
    return this.http.post<ApiResponse>(url, data)
      .pipe(
        map(response => {
          if (response.success) {
            return response.success;
          }
          throw response;
        })
      );
  }


  save(companyID: number, table: Table): Observable<Table | undefined> {
    const url = environment.apiBase + `table/save?companyID=${companyID}`;
    return this.http.post<GenericItemApiResponse<Table>>(url, table)
      .pipe(
        map(response => {
          if (response.success) {
            return response.item;
          }
          return undefined;
        }),
        tap(s => {
          if (s) {
            this.saved.next(s);
          }
        })
      );
  }


  delete(table: Table): Observable<boolean> {
    const url = environment.apiBase + `table/delete`;
    return this.http.post<ApiResponse>(url, table)
      .pipe(
        map(response => response.success),
        tap(s => this.deleted.next(table))
      );
  }

  getTotal(companyID: number, tableID: number) {

    const url = environment.apiBase + `table/total?companyID=${companyID}&tableID=${tableID}`;

    return this.http.get<GenericItemApiResponse<TableTotal>>(url,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.tableTotal } }).pipe(
      map(
        data => {
          if (data.success) {
            return data.item;
          }
          else {
            throw data.errorCode;
          }
        }
      )
    );
  }


  cancelCallWaiter(tableID: number) {
    const url = environment.apiBase + `table/CancelCallWaiter?tableID=${tableID}`;
    return this.http.post<ApiResponse>(url, null)
      .pipe(
        map(response => {
          return response.success;
        })
      );
  }

  activeWaiterCalls(tableID: number) {
    const url = environment.apiBase + `table/activeWaiterCalls?tableID=${tableID}`;
    return this.http.get<GenericListApiResponse<WaiterCall>>(url,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.waiterCalls } })
      .pipe(
        map(response => {
          if (response.success) {
            return response.items;
          }

          throw response;
        })
      );
  }

  cancelRequestPayment(tableID: number) {
    const url = environment.apiBase + `table/CancelRequestPayment?tableID=${tableID}`;
    return this.http.post<ApiResponse>(url, null)
      .pipe(
        map(response => {
          return response.success;
        })
      );
  }


  get hasPrinter() {
    return SHELL_APP_CONFIG.hasPrinter;
  }


  /**
   * Naplata: sto sa grupom (`tableID > 0`) ili samostalna porudžbina bez stola
   * (`tableID = 0`, prosledi `orderID`). Server (`table/charge`) grana i šalje POS-u
   * odgovarajuću poruku (ChargeTable vs ChargeOrder).
   */
  charge(p: ChargeParams): Observable<ChargeTableResponse> {
    // TODO(prod-api): PRIVREMENO — obrisati ovu granu (i _helpers/backendCompat.ts) kad produkcioni
    // backend pređe na novi API. Produkcija još nema table/charge bez stola (puca na "sto 0"), pa
    // samostalnu porudžbinu van test servera šaljemo na stari table/chargeSingleOrder, koji postoji
    // i na novom backendu. Spisak svega za brisanje: BackOffice docs/prod-api-cleanup.md.
    if ((p.tableID ?? 0) <= 0 && p.orderID && !useUnifiedCharge()) {
      return this.chargeSingleOrder(p.orderID, p.payments, p.total, p.tip, p.client, p.vpfrData, p.buyerID, p.buyerCostCenterId);
    }

    const url = environment.apiBase + `table/charge`;
    var data: ChargeTable = {
      tableID: p.tableID,
      orderID: p.orderID,
      payments: p.payments,
      tip: p.tip,
      group: p.group,
      total: p.total,
      client: p.client,
      customer: p.customer,
      printInvoice: !this.hasPrinter,
      printLineCharacterCount: (SHELL_APP_CONFIG.printLineCharacterCount || 0),
      vpfrData: p.vpfrData,
      buyerID: p.buyerID,
      buyerCostCenterId: p.buyerCostCenterId
    };

    // Pouzdano slanje: u native shell-u ide kroz native (outbox + retry ako server nije dostupan);
    // u web-u direktan HTTP. Server je idempotentan (dedup po broju računa / IsPaid), pa je retry bezbedan.
    return from(this.reliableHttp.send('POST', url, data, { 'SkipApiResponseInterceptor': '1' })).pipe(
      map(res => {
        // Offline: native je stavio charge u outbox i poslaće ga kasnije. Fiskalni račun je već
        // odštampan klijentski, pa naplatu tretiramo kao (optimistički) uspelu.
        if (res.queued) {
          return { journal: '', verificationURL: '', invoiceID: '' } as ChargeTableResponse;
        }

        const val = (typeof res.body === 'string' ? JSON.parse(res.body) : res.body) as GenericItemApiResponse<ChargeTableResponse>;
        if (val?.success) {
          return val.item;
        }

        throw val;
      })
    );
  }


  /**
   * @deprecated Naplata je objedinjena na `table/charge`; za samostalnu porudžbinu (bez stola)
   * koristi `charge({ tableID: 0, orderID, … })`. Zadržano radi kompatibilnosti (drugi app-ovi /
   * rollback) dok se ne ukloni u contract fazi zajedno sa `table/chargeSingleOrder`.
   */
  chargeSingleOrder(orderID: number, payments: Payment[], total: number, tip: number, client?: Client, vpfrData?: any, buyerID?: string, buyerCostCenterId?: string | null ) {
    this.logger.debug('TableService.chargeSingleOrder', orderID, payments, total, client);

    const url = environment.apiBase + `table/chargeSingleOrder`;
    var data: ChargeOrder = {
      orderID: orderID,
      payments: payments,
      total: total,
      tip: tip,
      client: client,
      printInvoice: !this.hasPrinter,
      printLineCharacterCount: (SHELL_APP_CONFIG.printLineCharacterCount || 0),
      vpfrData: vpfrData,
      buyerID: buyerID,
      buyerCostCenterId: buyerCostCenterId
    };

    return this.http.post<GenericItemApiResponse<ChargeTableResponse>>(url, data).pipe(
      map(val => {
        this.logger.debug('TableService.chargeSingleOrder result', val);
        if (val.success) {
          return val.item;
        }

        throw val;
      })
    );
  }

}
